import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { ArrowLeft, Plus, Trash2, Search, UserPlus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { DatePickerField } from '../components/DatePickerField';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ProjectForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLeadMode = searchParams.get('status') === 'lead';
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    nazwa: '',
    klient: '',
    adres_inwestycji: '',
    telefon_klienta: '',
    email_klienta: '',
    total_offer: '',
    costs: {
      okna: '',
      drzwi: '',
      montaz: '',
      dodatkowe: []
    },
    status: 'lead',
    description: '',
    owner_id: '',
    measurement_date: '',
    delivery_date: '',
    client_id: '',
    source: ''
  });

  // Client autocomplete state
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => { fetchUsers(); }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) setShowClientDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Błąd pobierania użytkowników');
    }
  };

  const searchClients = useCallback(async (q) => {
    if (q.length < 2) { setClientResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/clients/search`, { params: { q } });
      setClientResults(res.data);
      setShowClientDropdown(true);
    } catch (err) { console.error("Search error:", err); setClientResults([]); }
  }, []);

  const handleClientSearchChange = (e) => {
    const val = e.target.value;
    setClientSearch(val);
    setSelectedClient(null);
    setFormData(prev => ({ ...prev, klient: val, client_id: '' }));
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchClients(val), 300);
  };

  const selectClient = (c) => {
    setSelectedClient(c);
    setClientSearch(c.nazwa);
    setShowClientDropdown(false);
    setFormData(prev => ({
      ...prev,
      klient: c.nazwa,
      email_klienta: c.email || prev.email_klienta,
      telefon_klienta: c.telefon || prev.telefon_klienta,
      adres_inwestycji: c.adres || prev.adres_inwestycji,
      client_id: c.id
    }));
    toast.success(`Dane klienta "${c.nazwa}" wypełnione`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let clientId = formData.client_id;

      // Auto-create new client if no existing client was selected
      if (!clientId && formData.klient.trim()) {
        try {
          const clientRes = await axios.post(`${API_URL}/clients`, {
            nazwa: formData.klient,
            email: formData.email_klienta || '',
            telefon: formData.telefon_klienta || '',
            adres: formData.adres_inwestycji || ''
          });
          clientId = clientRes.data.id;
        } catch (err) { console.error("Client search error:", err);
          // Non-blocking: proceed even if client creation fails
        }
      }

      const payload = {
        ...formData,
        client_id: clientId || null,
        total_offer: parseFloat(formData.total_offer) || 0,
        costs: {
          okna: parseFloat(formData.costs.okna) || 0,
          drzwi: parseFloat(formData.costs.drzwi) || 0,
          montaz: parseFloat(formData.costs.montaz) || 0,
          dodatkowe: formData.costs.dodatkowe.map(item => ({
            opis: item.opis,
            kwota: parseFloat(item.kwota) || 0
          }))
        }
      };

      await axios.post(`${API_URL}/projects`, payload);
      toast.success('Projekt utworzony pomyślnie');
      navigate(isLeadMode ? '/leads' : '/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd tworzenia projektu');
    } finally {
      setLoading(false);
    }
  };

  const calculateMargin = () => {
    const total = parseFloat(formData.total_offer) || 0;
    const costs = 
      (parseFloat(formData.costs.okna) || 0) +
      (parseFloat(formData.costs.drzwi) || 0) +
      (parseFloat(formData.costs.montaz) || 0) +
      formData.costs.dodatkowe.reduce((sum, item) => sum + (parseFloat(item.kwota) || 0), 0);
    return total - costs;
  };

  const calculateMarginPercent = () => {
    const total = parseFloat(formData.total_offer) || 0;
    const margin = calculateMargin();
    return total > 0 ? (margin / total * 100) : 0;
  };

  const addDodatkowyKoszt = () => {
    setFormData({
      ...formData,
      costs: {
        ...formData.costs,
        dodatkowe: [...formData.costs.dodatkowe, { id: `dk_${Date.now()}`, opis: '', kwota: '' }]
      }
    });
  };

  const removeDodatkowyKoszt = (index) => {
    const newDodatkowe = formData.costs.dodatkowe.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      costs: {
        ...formData.costs,
        dodatkowe: newDodatkowe
      }
    });
  };

  const updateDodatkowyKoszt = (index, field, value) => {
    const newDodatkowe = [...formData.costs.dodatkowe];
    newDodatkowe[index][field] = value;
    setFormData({
      ...formData,
      costs: {
        ...formData.costs,
        dodatkowe: newDodatkowe
      }
    });
  };

  return (
    <Layout>
      <div className="p-8 lg:p-12">
        <div className="mb-8">
          <Button
            onClick={() => navigate('/projects')}
            data-testid="back-to-projects-button"
            className="bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border border-[var(--crm-border)] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Powrót
          </Button>
          <h1 className="text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight">Nowy Projekt</h1>
          <p className="text-[var(--crm-text-secondary)] mt-2">Dodaj nowy projekt do systemu</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
                <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">Informacje Podstawowe</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="nazwa" className="text-[var(--crm-text-secondary)]">Nazwa Projektu *</Label>
                    <Input
                      id="nazwa"
                      value={formData.nazwa}
                      onChange={(e) => setFormData({...formData, nazwa: e.target.value})}
                      data-testid="project-name-input"
                      required
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                    />
                  </div>

                  <div ref={clientRef} className="relative">
                    <Label htmlFor="klient" className="text-[var(--crm-text-secondary)]">Klient *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--crm-text-secondary)] pointer-events-none" />
                      <Input
                        id="klient"
                        value={clientSearch || formData.klient}
                        onChange={handleClientSearchChange}
                        onFocus={() => clientSearch.length >= 2 && setShowClientDropdown(true)}
                        data-testid="project-client-input"
                        placeholder="Wpisz nazwę klienta (min. 2 znaki)..."
                        required
                        autoComplete="off"
                        className="pl-10 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                      />
                      {selectedClient && (
                        <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
                      )}
                    </div>
                    {showClientDropdown && clientResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-[var(--crm-bg-main)] border border-[var(--crm-border)] rounded-lg shadow-xl max-h-48 overflow-y-auto"
                        data-testid="client-autocomplete-dropdown">
                        {clientResults.map(c => (
                          <button key={c.id} type="button" onClick={() => selectClient(c)}
                            className="w-full text-left px-4 py-2.5 hover:bg-[#FF6B35]/10 transition-colors border-b border-[var(--crm-border)]/50 last:border-b-0"
                            data-testid={`client-option-${c.id}`}>
                            <span className="text-[var(--crm-text-primary)] font-medium text-sm">{c.nazwa}</span>
                            <span className="text-[var(--crm-text-secondary)] text-xs block">
                              {[c.email, c.telefon].filter(Boolean).join(' · ') || 'Brak danych kontaktowych'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {!selectedClient && clientSearch.length >= 2 && clientResults.length === 0 && showClientDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-[var(--crm-bg-main)] border border-[var(--crm-border)] rounded-lg shadow-xl p-3"
                        data-testid="client-new-hint">
                        <p className="text-[var(--crm-text-secondary)] text-sm flex items-center gap-2">
                          <UserPlus className="w-4 h-4 text-[#FF6B35]" />
                          Nowy klient &ldquo;{clientSearch}&rdquo; zostanie utworzony automatycznie
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="adres_inwestycji" className="text-[var(--crm-text-secondary)]">Adres Inwestycji</Label>
                    <Input
                      id="adres_inwestycji"
                      placeholder="ul. Przykładowa 123, Warszawa"
                      value={formData.adres_inwestycji}
                      onChange={(e) => setFormData({...formData, adres_inwestycji: e.target.value})}
                      data-testid="project-address-input"
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                    />
                    {formData.adres_inwestycji && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.adres_inwestycji)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#FF6B35] text-sm hover:underline inline-flex items-center gap-1 mt-1"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        Zobacz na mapie Google Maps
                      </a>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="telefon_klienta" className="text-[var(--crm-text-secondary)]">Telefon Klienta</Label>
                      <Input
                        id="telefon_klienta"
                        type="tel"
                        placeholder="+48 123 456 789"
                        value={formData.telefon_klienta}
                        onChange={(e) => setFormData({...formData, telefon_klienta: e.target.value})}
                        data-testid="project-phone-input"
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email_klienta" className="text-[var(--crm-text-secondary)]">Email Klienta</Label>
                      <Input
                        id="email_klienta"
                        type="email"
                        placeholder="klient@example.com"
                        value={formData.email_klienta}
                        onChange={(e) => setFormData({...formData, email_klienta: e.target.value})}
                        data-testid="project-email-input"
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description" className="text-[var(--crm-text-secondary)]">Opis</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      data-testid="project-description-input"
                      rows={3}
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                    />
                  </div>

                  <div>
                    <Label className="text-[var(--crm-text-secondary)]">Źródło pozyskania</Label>
                    <Select value={formData.source} onValueChange={(val) => setFormData({...formData, source: val})}>
                      <SelectTrigger data-testid="project-source-select" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectValue placeholder="Wybierz źródło..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectItem value="Trasa">Trasa</SelectItem>
                        <SelectItem value="Polecenie">Polecenie</SelectItem>
                        <SelectItem value="Networking">Networking</SelectItem>
                        <SelectItem value="Przetarg">Przetarg</SelectItem>
                        <SelectItem value="Internet">Internet</SelectItem>
                        <SelectItem value="Inne">Inne</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="status" className="text-[var(--crm-text-secondary)]">Status *</Label>
                    <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                      <SelectTrigger data-testid="project-status-select" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectItem value="lead">Lead/do pomiaru</SelectItem>
                        <SelectItem value="akceptacja">Akceptacja</SelectItem>
                        <SelectItem value="brak_akceptacji">Brak akceptacji</SelectItem>
                        <SelectItem value="oplacone">Opłacone</SelectItem>
                        <SelectItem value="dostawa">Dostawa</SelectItem>
                        <SelectItem value="w_realizacji">W realizacji</SelectItem>
                        <SelectItem value="zakonczone">Zakończono</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Data pomiaru</Label>
                      <DatePickerField
                        value={formData.measurement_date}
                        onChange={(val) => setFormData({...formData, measurement_date: val})}
                        placeholder="Wybierz datę pomiaru"
                        data-testid="project-measurement-date"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Data dostawy</Label>
                      <DatePickerField
                        value={formData.delivery_date}
                        onChange={(val) => setFormData({...formData, delivery_date: val})}
                        placeholder="Wybierz datę dostawy"
                        data-testid="project-delivery-date"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="owner" className="text-[var(--crm-text-secondary)]">Właściciel/Manager Projektu</Label>
                    <Select value={formData.owner_id} onValueChange={(val) => setFormData({...formData, owner_id: val})}>
                      <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectValue placeholder="Wybierz właściciela" />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        {users.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.initials} - {user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>

              {/* Costs section - conditional rendering based on mode */}
              {isLeadMode ? (
              <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
                <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">Wstępna wycena</h2>
                <div>
                  <Label htmlFor="lead_estimate" className="text-[var(--crm-text-secondary)]">Wstępna kwota z oferty (zł)</Label>
                  <p className="text-[var(--crm-text-muted)] text-xs mb-2">Szacunkowa wartość — szczegółowe koszty uzupełnisz po zmianie statusu na Zlecenie</p>
                  <Input
                    id="lead_estimate"
                    type="number"
                    step="0.01"
                    value={formData.total_offer}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({
                        ...formData,
                        total_offer: val,
                        costs: {
                          okna: 0, drzwi: 0, montaz: 0,
                          dodatkowe: val ? [{ id: `dk_${Date.now()}`, opis: 'Wstępna wycena', kwota: val }] : []
                        }
                      });
                    }}
                    data-testid="lead-estimate-input"
                    placeholder="np. 45000"
                    className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono text-lg h-12"
                  />
                </div>
              </Card>
              ) : (
              <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
                <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">Finanse</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="total_offer" className="text-[var(--crm-text-secondary)]">Całkowita Oferta (zł)</Label>
                    <Input
                      id="total_offer"
                      type="number"
                      step="0.01"
                      value={formData.total_offer}
                      onChange={(e) => setFormData({...formData, total_offer: e.target.value})}
                      data-testid="project-offer-input"
                      placeholder="0.00"
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="okna" className="text-[var(--crm-text-secondary)]">Okna (zł)</Label>
                      <Input
                        id="okna"
                        type="number"
                        step="0.01"
                        value={formData.costs.okna}
                        onChange={(e) => setFormData({...formData, costs: {...formData.costs, okna: e.target.value}})}
                        data-testid="project-cost-okna-input"
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                      />
                    </div>

                    <div>
                      <Label htmlFor="drzwi" className="text-[var(--crm-text-secondary)]">Drzwi (zł)</Label>
                      <Input
                        id="drzwi"
                        type="number"
                        step="0.01"
                        value={formData.costs.drzwi}
                        onChange={(e) => setFormData({...formData, costs: {...formData.costs, drzwi: e.target.value}})}
                        data-testid="project-cost-drzwi-input"
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                      />
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="montaz" className="text-[var(--crm-text-secondary)]">Montaż obcy FV (zł)</Label>
                      <Input
                        id="montaz"
                        type="number"
                        step="0.01"
                        value={formData.costs.montaz}
                        onChange={(e) => setFormData({...formData, costs: {...formData.costs, montaz: e.target.value}})}
                        data-testid="project-cost-montaz-input"
                        className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                      />
                    </div>
                  </div>

                  <div className="border-t border-[var(--crm-border)] pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-[var(--crm-text-secondary)]">Dodatkowe Koszty</Label>
                      <Button
                        type="button"
                        onClick={addDodatkowyKoszt}
                        data-testid="add-additional-cost-button"
                        className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white text-sm h-8"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Dodaj Koszt
                      </Button>
                    </div>

                    {formData.costs.dodatkowe.length === 0 ? (
                      <p className="text-[var(--crm-text-secondary)] text-sm text-center py-4">
                        Brak dodatkowych kosztów. Kliknij przycisk powyżej aby dodać.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {formData.costs.dodatkowe.map((item, index) => (
                          <div key={item.id || `addcost-${index}`} className="flex gap-2 items-start">
                            <div className="flex-1">
                              <Input
                                placeholder="Opis kosztu (np. Transport, Usługi dodatkowe)"
                                value={item.opis}
                                onChange={(e) => updateDodatkowyKoszt(index, 'opis', e.target.value)}
                                data-testid={`additional-cost-desc-${index}`}
                                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
                              />
                            </div>
                            <div className="w-32">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Kwota"
                                value={item.kwota}
                                onChange={(e) => updateDodatkowyKoszt(index, 'kwota', e.target.value)}
                                data-testid={`additional-cost-amount-${index}`}
                                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] font-mono"
                              />
                            </div>
                            <Button
                              type="button"
                              onClick={() => removeDodatkowyKoszt(index)}
                              data-testid={`remove-additional-cost-${index}`}
                              className="bg-red-600 hover:bg-red-700 text-white h-10 w-10 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
              )}
            </div>

            <div>
              <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 sticky top-8">
                <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">{isLeadMode ? 'Zapis' : 'Podsumowanie'}</h2>
                <div className="space-y-4">
                  {!isLeadMode && (
                  <>
                  <div className="flex justify-between items-center py-2 border-b border-[var(--crm-border)]">
                    <span className="text-[var(--crm-text-secondary)]">Wartość oferty</span>
                    <span className="text-[var(--crm-text-primary)] font-mono font-semibold">
                      {parseFloat(formData.total_offer || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-[var(--crm-border)]">
                    <span className="text-[var(--crm-text-secondary)]">Koszty</span>
                    <span className="text-[var(--crm-text-primary)] font-mono font-semibold">
                      {(
                        (parseFloat(formData.costs.okna) || 0) +
                        (parseFloat(formData.costs.drzwi) || 0) +
                        (parseFloat(formData.costs.montaz) || 0) +
                        formData.costs.dodatkowe.reduce((sum, item) => sum + (parseFloat(item.kwota) || 0), 0)
                      ).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-3 bg-[var(--crm-bg-panel)] px-4 rounded-md">
                    <span className="text-emerald-400 font-semibold">Marża</span>
                    <span className="text-emerald-400 font-mono font-bold text-lg">
                      {calculateMargin().toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-3 bg-[var(--crm-bg-panel)] px-4 rounded-md">
                    <span className="text-amber-400 font-semibold">Marża %</span>
                    <span className="text-amber-400 font-mono font-bold text-lg">
                      {calculateMarginPercent().toFixed(2)}%
                    </span>
                  </div>
                  </>
                  )}

                  {isLeadMode && (
                    <div>
                      {formData.total_offer ? (
                        <div className="flex justify-between items-center py-3 bg-[var(--crm-bg-panel)] px-4 rounded-md mb-3">
                          <span className="text-amber-400 font-semibold">Wstępna kwota</span>
                          <span className="text-amber-400 font-mono font-bold text-lg">
                            {parseFloat(formData.total_offer || 0).toLocaleString('pl-PL')} zł
                          </span>
                        </div>
                      ) : null}
                      <p className="text-[var(--crm-text-secondary)] text-sm">Lead zostanie utworzony ze statusem &ldquo;Lead/do pomiaru&rdquo;. Koszty można uzupełnić później.</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-project-button"
                    className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white font-semibold mt-4"
                  >
                    {loading ? 'Tworzenie...' : isLeadMode ? 'Utwórz Lead' : 'Utwórz Projekt'}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};